import Emitter from "@/services/Emitter";

/**
 * ActivityTracker monitors user activity (mouse, keyboard, touch, visibility)
 * and emits events for WebSocket idle management.
 * Disconnects WebSocket after 15 minutes of inactivity.
 */
class ActivityTracker {
    private idleTimeoutMs: number = 15 * 60 * 1000; // 15 minutes
    private throttleMs: number = 30000; // 30 seconds
    private lastActivity: number = Date.now();
    private lastEmit: number = 0;
    private activityTimer: number | null = null;
    private listeners: Array<() => void> = [];
    private isTracking = false;

    /**
     * Start tracking user activity
     */
    public startTracking(): void {
        if (this.isTracking) return;
        this.isTracking = true;

        // Bind methods to preserve context
        const throttledActivity = this.throttle(() => this.markActivity(), this.throttleMs);
        const visibilityHandler = () => this.handleVisibilityChange();

        // Track various activity events
        window.addEventListener("mousemove", throttledActivity);
        window.addEventListener("click", throttledActivity);
        window.addEventListener("keydown", throttledActivity);
        window.addEventListener("touchstart", throttledActivity);
        window.addEventListener("scroll", throttledActivity);
        document.addEventListener("visibilitychange", visibilityHandler);

        // Store cleanup functions
        this.listeners = [
            () => window.removeEventListener("mousemove", throttledActivity),
            () => window.removeEventListener("click", throttledActivity),
            () => window.removeEventListener("keydown", throttledActivity),
            () => window.removeEventListener("touchstart", throttledActivity),
            () => window.removeEventListener("scroll", throttledActivity),
            () => document.removeEventListener("visibilitychange", visibilityHandler),
        ];

        // Start the idle timer
        this.resetIdleTimer();
        console.log("[ActivityTracker] Started tracking user activity");
    }

    /**
     * Stop tracking user activity
     */
    public stopTracking(): void {
        if (!this.isTracking) return;
        this.isTracking = false;

        // Remove all event listeners
        this.listeners.forEach((cleanup) => cleanup());
        this.listeners = [];

        // Clear idle timer
        if (this.activityTimer) {
            clearTimeout(this.activityTimer);
            this.activityTimer = null;
        }

        console.log("[ActivityTracker] Stopped tracking user activity");
    }

    /**
     * Mark activity detected - resets idle timer and emits event
     */
    public markActivity(): void {
        const now = Date.now();
        this.lastActivity = now;

        // Only emit if throttled interval has passed
        if (now - this.lastEmit >= this.throttleMs) {
            this.lastEmit = now;
            Emitter.emit("user:activity");
            console.log("[ActivityTracker] Activity detected");
        }

        this.resetIdleTimer();
    }

    /**
     * Handle page visibility changes (tab switch)
     */
    private handleVisibilityChange(): void {
        if (document.visibilityState === "visible") {
            // User returned to tab - check if we've been idle
            const now = Date.now();
            const idleTime = now - this.lastActivity;

            if (idleTime >= this.idleTimeoutMs) {
                // We've been idle - emit event to trigger reconnect
                Emitter.emit("user:activity");
                console.log("[ActivityTracker] User returned after idle period");
            }

            this.markActivity();
        }
    }

    /**
     * Reset the idle timeout timer
     */
    private resetIdleTimer(): void {
        if (this.activityTimer) {
            clearTimeout(this.activityTimer);
        }

        this.activityTimer = window.setTimeout(() => {
            Emitter.emit("user:idle");
            console.log("[ActivityTracker] User idle for 15 minutes");
        }, this.idleTimeoutMs);
    }

    /**
     * Check if user is currently idle
     */
    public isIdle(): boolean {
        return Date.now() - this.lastActivity >= this.idleTimeoutMs;
    }

    /**
     * Get time since last activity in milliseconds
     */
    public getIdleTime(): number {
        return Date.now() - this.lastActivity;
    }

    /**
     * Throttle function execution
     */
    private throttle(fn: () => void, delay: number): () => void {
        let lastCall = 0;
        return () => {
            const now = Date.now();
            if (now - lastCall >= delay) {
                lastCall = now;
                fn();
            }
        };
    }
}

// Export singleton instance
export default new ActivityTracker();
